import { supabase } from './supabase'

export interface SecurityEvent {
  id: string
  user_id?: string
  event_type: 'login' | 'logout' | 'role_change' | 'sensitive_action' | 'ip_blocked' | 'ip_unblocked'
  ip_address?: string
  details?: any
  severity: 'low' | 'medium' | 'high' | 'critical'
  created_at: string
}

export interface FailedLogin {
  id: string
  email: string
  ip_address: string
  user_agent?: string
  reason: 'invalid_credentials' | 'account_locked' | 'suspicious_activity'
  attempted_at: string
  location?: string
}

export interface BlockedIP {
  id: string
  ip_address: string
  reason: string
  blocked_at: string
  blocked_by?: string
  is_active: boolean
}

export interface UserSession {
  id: string
  user_id: string
  session_token: string
  ip_address?: string
  user_agent?: string
  location?: string
  created_at: string
  last_activity: string
  is_active: boolean
}

class SecurityService {
  // Get client IP address (simplified for now)
  private getClientIP(): string {
    // In a real app, this would get the actual client IP
    // For now, return a mock IP
    return '192.168.1.100'
  }

  // Log a failed login attempt
  async logFailedLogin(email: string, reason: 'invalid_credentials' | 'account_locked' | 'suspicious_activity', userAgent?: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('failed_logins')
        .insert({
          email,
          ip_address: this.getClientIP(),
          user_agent: userAgent,
          reason
        })

      if (error) {
        console.error('Error logging failed login:', error)
      }
    } catch (error) {
      console.error('Error logging failed login:', error)
    }
  }

  // Check if an IP address is blocked
  async isIPBlocked(ipAddress?: string): Promise<boolean> {
    try {
      const ip = ipAddress || this.getClientIP()
      
      const { data, error } = await supabase
        .from('blocked_ips')
        .select('id')
        .eq('ip_address', ip)
        .eq('is_active', true)
        .single()

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking IP block status:', error)
        return false
      }

      return !!data
    } catch (error) {
      console.error('Error checking IP block status:', error)
      return false
    }
  }

  // Block an IP address
  async blockIP(ipAddress: string, reason: string, blockedBy?: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('blocked_ips')
        .upsert({
          ip_address: ipAddress,
          reason,
          blocked_by: blockedBy,
          is_active: true
        })

      if (error) {
        console.error('Error blocking IP:', error)
        return false
      }

      // Log the security event
      await this.logSecurityEvent('ip_blocked', {
        ip_address: ipAddress,
        reason
      }, 'medium')

      return true
    } catch (error) {
      console.error('Error blocking IP:', error)
      return false
    }
  }

  // Unblock an IP address
  async unblockIP(ipAddress: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('blocked_ips')
        .update({ is_active: false })
        .eq('ip_address', ipAddress)

      if (error) {
        console.error('Error unblocking IP:', error)
        return false
      }

      // Log the security event
      await this.logSecurityEvent('ip_unblocked', {
        ip_address: ipAddress
      }, 'low')

      return true
    } catch (error) {
      console.error('Error unblocking IP:', error)
      return false
    }
  }

  // Get failed login count for an email
  async getFailedLoginCount(email: string, hours: number = 24): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('failed_logins')
        .select('*', { count: 'exact', head: true })
        .eq('email', email)
        .gte('attempted_at', new Date(Date.now() - hours * 60 * 60 * 1000).toISOString())

      if (error) {
        console.error('Error getting failed login count:', error)
        return 0
      }

      return count || 0
    } catch (error) {
      console.error('Error getting failed login count:', error)
      return 0
    }
  }

  // Log a security event
  async logSecurityEvent(
    eventType: SecurityEvent['event_type'],
    details?: any,
    severity: 'low' | 'medium' | 'high' | 'critical' = 'low',
    userId?: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('security_events')
        .insert({
          user_id: userId,
          event_type: eventType,
          ip_address: this.getClientIP(),
          details,
          severity
        })

      if (error) {
        console.error('Error logging security event:', error)
      }
    } catch (error) {
      console.error('Error logging security event:', error)
    }
  }

  // Get all failed logins (for admin dashboard)
  async getFailedLogins(limit: number = 50): Promise<FailedLogin[]> {
    try {
      const { data, error } = await supabase
        .from('failed_logins')
        .select('*')
        .order('attempted_at', { ascending: false })
        .limit(limit)

      if (error) {
        console.error('Error getting failed logins:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error getting failed logins:', error)
      return []
    }
  }

  // Get all blocked IPs (for admin dashboard)
  async getBlockedIPs(): Promise<BlockedIP[]> {
    try {
      const { data, error } = await supabase
        .from('blocked_ips')
        .select('*')
        .order('blocked_at', { ascending: false })

      if (error) {
        console.error('Error getting blocked IPs:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error getting blocked IPs:', error)
      return []
    }
  }

  // Get all user sessions (for admin dashboard)
  async getUserSessions(): Promise<UserSession[]> {
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('is_active', true)
        .order('last_activity', { ascending: false })

      if (error) {
        console.error('Error getting user sessions:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error getting user sessions:', error)
      return []
    }
  }

  // Get security events (for admin dashboard)
  async getSecurityEvents(limit: number = 50): Promise<SecurityEvent[]> {
    try {
      const { data, error } = await supabase
        .from('security_events')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) {
        console.error('Error getting security events:', error)
        return []
      }

      return data || []
    } catch (error) {
      console.error('Error getting security events:', error)
      return []
    }
  }

  // Terminate a user session
  async terminateSession(sessionId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_sessions')
        .update({ is_active: false })
        .eq('id', sessionId)

      if (error) {
        console.error('Error terminating session:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error terminating session:', error)
      return false
    }
  }

  // Create a new user session
  async createUserSession(userId: string, sessionToken: string, userAgent?: string): Promise<boolean> {
    try {
      // Generate a shorter session identifier from the token
      const sessionId = this.generateSessionId(sessionToken)
      
      // First check if session already exists for this user
      const { data: existingSessions, error: queryError } = await supabase
        .from('user_sessions')
        .select('id, session_token')
        .eq('user_id', userId)
        .eq('is_active', true)

      if (queryError) {
        console.error('Error querying existing sessions:', queryError)
        // If we can't query, just try to create a new session
        const { error: insertError } = await supabase
          .from('user_sessions')
          .insert({
            user_id: userId,
            session_token: sessionId,
            ip_address: this.getClientIP(),
            user_agent: userAgent || navigator.userAgent
          })

        if (insertError) {
          console.error('Error creating user session:', insertError)
          return false
        }
        return true
      }

      const existingSession = existingSessions?.[0]

      if (existingSession) {
        // If the session token is different, update it
        if (existingSession.session_token !== sessionId) {
          const { error: updateError } = await supabase
            .from('user_sessions')
            .update({ 
              session_token: sessionId,
              last_activity: new Date().toISOString(),
              user_agent: userAgent || navigator.userAgent
            })
            .eq('id', existingSession.id)

          if (updateError) {
            console.error('Error updating session token:', updateError)
            return false
          }
        } else {
          // Session token matches, just update activity
          await this.updateSessionActivityById(existingSession.id)
        }
        return true
      }

      // Create new session
      const { error } = await supabase
        .from('user_sessions')
        .insert({
          user_id: userId,
          session_token: sessionId, // Use shorter identifier
          ip_address: this.getClientIP(),
          user_agent: userAgent || navigator.userAgent
        })

      if (error) {
        console.error('Error creating user session:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error creating user session:', error)
      return false
    }
  }

  // Generate a shorter session identifier
  private generateSessionId(sessionToken: string): string {
    // Create a hash of the session token to use as identifier
    // This avoids storing the full JWT token
    const hash = sessionToken.split('.').pop() || sessionToken
    return hash.substring(0, 32) // Limit to 32 characters
  }

  // Update session activity by ID instead of token
  async updateSessionActivityById(sessionId: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('user_sessions')
        .update({ last_activity: new Date().toISOString() })
        .eq('id', sessionId)

      if (error) {
        console.error('Error updating session activity:', error)
        return false
      }

      return true
    } catch (error) {
      console.error('Error updating session activity:', error)
      return false
    }
  }

  // Update session activity (legacy method - kept for compatibility)
  async updateSessionActivity(sessionToken: string): Promise<boolean> {
    try {
      // Instead of querying by session_token, get the current user and update their active session
      const { data: authUser } = await supabase.auth.getUser()
      if (!authUser?.user?.id) {
        return false
      }

      // Find any active session for the current user
      const { data: activeSessions, error: findError } = await supabase
        .from('user_sessions')
        .select('id')
        .eq('user_id', authUser.user.id)
        .eq('is_active', true)

      if (findError) {
        console.error('Error finding active sessions:', findError)
        return false
      }

      if (!activeSessions || activeSessions.length === 0) {
        return false
      }

      // Update the first active session
      const { error: updateError } = await supabase
        .from('user_sessions')
        .update({ last_activity: new Date().toISOString() })
        .eq('id', activeSessions[0].id)

      if (updateError) {
        console.error('Error updating session activity:', updateError)
        return false
      }

      return true
    } catch (error) {
      console.error('Error updating session activity:', error)
      return false
    }
  }

  // Check for suspicious activity
  async checkSuspiciousActivity(email: string): Promise<boolean> {
    try {
      const failedCount = await this.getFailedLoginCount(email, 1) // Last hour
      return failedCount >= 5 // Consider suspicious if 5+ failed attempts in 1 hour
    } catch (error) {
      console.error('Error checking suspicious activity:', error)
      return false
    }
  }
}

export const securityService = new SecurityService() 